import React, { useState } from 'react';
import { sendContactForm, UserInfo } from '../Utility/sharedFunctions';
import '../CSS/HomePage.css';

const HomePage: React.FC = () => {
  const [activeService, setActiveService] = useState('autonomous');
  const [activeTab, setActiveTab] = useState('workflow');
  const [formData, setFormData] = useState<UserInfo>({
    name: '',
    email: '',
    phoneNumber: '',
    inquiry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Reset tab when service changes
  const handleServiceChange = (service: string) => {
    setActiveService(service);
    // Reset to first tab of the new service
    if (service === 'enterprise') {
      setActiveTab('strategy');
    } else if (service === 'chatbot') {
      setActiveTab('gpt');
    } else {
      setActiveTab('workflow');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await sendContactForm(formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        inquiry: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.querySelector('.homepage_services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToWhyChoose = () => {
    const whyChooseSection = document.querySelector('.why_zemantics');
    if (whyChooseSection) {
      whyChooseSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <div className="home-page">
      {/* Hero Banner with Video Background */}
      <div className="home_banner">
        <video
          className="banner-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/Assets/Home Page/2127-155244168.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="homepage-banner-overlay"></div>
        <div className="home_banner-content">
          <h1 className="home_banner-title">
            Infuse intelligence into your operations with <span className="highlight">Advanced AI</span>
          </h1>
          <div className="banner-buttons">
            <button className="home_banner-btn primary" onClick={scrollToServices}>Get Started</button>
            <button className="home_banner-btn secondary" onClick={scrollToWhyChoose}>Learn More</button>
          </div>
        </div>
      </div>

      {/* Zemantics Mantra Section */}
      <div className="zemantics_mantra">
        <p>Zemantics assists you in identifying and integrating cutting-edge AI solutions, guiding you seamlessly from inception to deployment and beyond.</p>
      </div>

      {/* Homepage Services Section */}
      <div className="homepage_services">
        <h2 className="services-title">Our Services</h2>
        <div className="services-container">
          {/* Left Side - Service Categories */}
          <div className="services-left">
            <div 
              className={`service-category ${activeService === 'autonomous' ? 'active' : ''}`}
              onClick={() => handleServiceChange('autonomous')}
            >
              <span className="service-number">01</span>
              <span className="service-name">Autonomous Agent Development</span>
            </div>
            <div 
              className={`service-category ${activeService === 'enterprise' ? 'active' : ''}`}
              onClick={() => handleServiceChange('enterprise')}
            >
              <span className="service-number">02</span>
              <span className="service-name">Enterprise AI & Automation Consulting</span>
            </div>
            <div 
              className={`service-category ${activeService === 'chatbot' ? 'active' : ''}`}
              onClick={() => handleServiceChange('chatbot')}
            >
              <span className="service-number">03</span>
              <span className="service-name">AI Chatbot Development</span>
            </div>
          </div>

          {/* Right Side - Service Details */}
          <div className="services-right">
            {/* Service Tabs */}
            <div className="service-tabs">
              {activeService === 'autonomous' && (
                <>
                  <button 
                    className={`service-tab ${activeTab === 'workflow' ? 'active' : ''}`}
                    onClick={() => setActiveTab('workflow')}
                  >
                    Workflow Automation
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'nlp' ? 'active' : ''}`}
                    onClick={() => setActiveTab('nlp')}
                  >
                    Natural Language to SQL
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'pipeline' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pipeline')}
                  >
                    Autonomous Data Pipeline
                  </button>
                </>
              )}
              {activeService === 'enterprise' && (
                <>
                  <button 
                    className={`service-tab ${activeTab === 'strategy' ? 'active' : ''}`}
                    onClick={() => setActiveTab('strategy')}
                  >
                    Strategy Development
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'usecase' ? 'active' : ''}`}
                    onClick={() => setActiveTab('usecase')}
                  >
                    Use Case Identification
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'feasibility' ? 'active' : ''}`}
                    onClick={() => setActiveTab('feasibility')}
                  >
                    Feasibility Assessments
                  </button>
                </>
              )}
              {activeService === 'chatbot' && (
                <>
                  <button 
                    className={`service-tab ${activeTab === 'gpt' ? 'active' : ''}`}
                    onClick={() => setActiveTab('gpt')}
                  >
                    GPT Development
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'knowledge' ? 'active' : ''}`}
                    onClick={() => setActiveTab('knowledge')}
                  >
                    Knowledge Response
                  </button>
                  <button 
                    className={`service-tab ${activeTab === 'prompt' ? 'active' : ''}`}
                    onClick={() => setActiveTab('prompt')}
                  >
                    Prompt Tuning
                  </button>
                </>
              )}
            </div>

            {/* Service Content */}
            <div className="service-content">
              {/* Autonomous Agent Development Content */}
              {activeService === 'autonomous' && activeTab === 'workflow' && (
                <div>
                  <p>In today's digital era, efficiency is the backbone of any successful business. At Zemantics, we harness the power of Autonomous Agents to revolutionize your workflow processes. Our Workflow Automation solution goes beyond traditional task automation; it incorporates intelligent agents that can make decisions, adapt to varying circumstances, and consistently optimize operations.</p>
                  <p>Through our deep understanding of AI and its potential, we design agents that can predictively automate tasks, reducing manual intervention and the chances of human error. Whether you're looking to streamline administrative processes, enhance data operations, or elevate customer interactions, our bespoke autonomous agents are built to integrate seamlessly into your environment.</p>
                </div>
              )}
              {activeService === 'autonomous' && activeTab === 'nlp' && (
                <div>
                  <p>In an age where data drives decisions, accessing and querying databases should be as intuitive as asking a question in plain English. At Zemantics, we pride ourselves on pushing the boundaries of what's possible. </p>
                  <p>Our solution transforms natural language queries into precise SQL statements, allowing users to retrieve, manipulate, and analyze data without the need to understand intricate SQL syntax. Empowering both non-tech professionals and experienced developers alike, we bridge the gap between human language and database queries, making data-driven decisions accessible to all.</p>
                </div>
              )}
              {activeService === 'autonomous' && activeTab === 'pipeline' && (
                <div>
                  <p>Data is the lifeblood of modern businesses, but its sheer volume and complexity can make it challenging to harness effectively. At Zemantics, we're pioneering the development of intricate data pipelines, transforming raw, disparate data into actionable insights. With our autonomous agent-driven solutions, we navigate the complexities of big data, ensuring fluidity and efficiency in data transition, processing, and analytics.</p>
                  <p>Our expertise lies in designing and deploying data pipelines that handle large-scale, multi-dimensional data. Whether you're dealing with real-time data streams, batch processing, or hybrid, our autonomous agents optimize processes, ensuring data integrity, and timely delivery for advanced analytics and machine learning applications.</p>
                </div>
              )}

              {/* Enterprise AI & Automation Consulting Content */}
              {activeService === 'enterprise' && activeTab === 'strategy' && (
                <div>
                  <p>At Zemantics, we leverage deep industry knowledge and cutting-edge insights to architect a coherent, actionable AI strategy tailored for your business. We understand that every enterprise has unique goals, challenges, and landscapes; our strategy development is designed to reflect that individuality.</p>
                  <p>We don't just skim the surface; our consultants dive deep into your organization's DNA, understanding its intricacies to pinpoint where AI can be a game-changer. From ideation to blueprinting, our strategy development process is a meticulous blend of your vision and our expertise.</p>
                </div>
              )}
              {activeService === 'enterprise' && activeTab === 'usecase' && (
                <div>
                  <p>Our AI’s Use Case Identification service is a specialized approach to uncover the most potent AI applications tailored for your enterprise. We believe in harnessing AI where it matters most, transforming potential into palpable impact.</p>
                  <p>Our consultants engage in a deep-dive analysis of your organization's operations, culture, and aspirations. By weaving together this understanding with our vast knowledge of AI advancements, we illuminate AI opportunities that resonate with your business objectives and industry dynamics.</p>
                </div>
              )}
              {activeService === 'enterprise' && activeTab === 'feasibility' && (
                <div>
                  <p>Venturing into AI initiatives without gauging their feasibility can lead to expended resources with little to show for it. Our Feasibility Assessments provide a rigorous examination of proposed AI projects, ensuring they stand on a foundation of practicality, viability, and potential return on investment.</p>
                  <p>With a meticulous approach, our consultants evaluate the technical, operational, and financial aspects of your AI aspirations. This comprehensive assessment ensures that every AI project you undertake is actionable and aligns with your business's infrastructure, goals, and resources.</p>
                </div>
              )}

              {/* AI Chatbot Development Content */}
              {activeService === 'chatbot' && activeTab === 'gpt' && (
                <div>
                  <p>The conversational AI landscape has been revolutionized with the advent of models like ChatGPT, Llama, Gemini, and Deepseek. We harness the unmatched capabilities of these groundbreaking models to create state-of-the-art chatbots that deliver human-like interactions, rich contextual understanding, and seamless user experiences.</p>
                  <p>Our expertise in GPT development places us at the forefront of chatbot innovation. By tapping into these models, we craft chatbots that are not just reactive but can also anticipate user needs, understand nuances, and offer solutions that feel genuinely intuitive.</p>
                </div>
              )}
              {activeService === 'chatbot' && activeTab === 'knowledge' && (
                <div>
                  <p>We all know that our data is vast and evolving. We can help you tap into your expansive information reservoir, allowing chatbots to provide accurate, up-to-date, and contextually relevant answers. We transcend traditional chatbot capabilities, moving beyond preset responses to offer insights drawn from a rich tapestry of data sources.</p>
                  <p>Our chatbots, equipped with Knowledge Response, become dynamic information hubs. They sift through volumes of data, discerning context, intent, and nuance to deliver not just any answer, but the right answer.</p>
                </div>
              )}
              {activeService === 'chatbot' && activeTab === 'prompt' && (
                <div>
                  <p>A chatbot is only as effective as the underlying model that powers it. We recognize the value of precision, responsiveness, and accuracy. Our Prompt Tuning service dives deep into the intricacies of your chatbot's machine learning model, optimizing its parameters for peak performance and ensuring it aligns seamlessly with your specific use-case demands.</p>
                  <p>Prompt tuning is both an art and a science. By employing advanced algorithms and a meticulous iterative process, we refine the prompts for the model, pushing its capabilities to the edge of perfection. The outcome? A chatbot that understands better, responds smarter, and engages more effectively.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Statistics Section */}
      <div className="ai_statistics">
        <div className="ai_statistics-content">
          <h2 className="ai_statistics-title">Unlock the Power of AI Agents and Embrace Full Automation</h2>
          <div className="ai_statistics-grid">
            <div className="stat-item">
              <h3 className="stat-title">Customer Engagements</h3>
              <div className="stat-value">100+</div>
              <p className="stat-description">AND GROWING</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-title">Agents Deployed</h3>
              <div className="stat-value">270+</div>
              <p className="stat-description">AND THRIVING</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-title">Total ARR Saved</h3>
              <div className="stat-value">$135+ M</div>
              <p className="stat-description">AND MULTIPLYING</p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Zemantics Section */}
      <div className="why_zemantics">
        <div className="why_zemantics-content">
          <h2 className="why_zemantics-title">
            Why Choose <span className="zemantics-gradient">Zemantics</span>
          </h2>
          <p className="why_zemantics-intro">
          Data Science is redefining the way businesses make decisions. It clearly is a new paradigm that will drive next-gen innovation. Our Data Scientist, Artificial Intelligence (AI) & Machine Learning (ML) team understands this and is uniquely positioned to provide expert consultation to organizations who want to empower their key decision makers with intelligent data. This is backed by collective years of experience and significant number of projects successfully completed. <br />Innovation driven organizations no longer need to worry about technical jargon because <b>team@Zemantics</b> bridges the gap between the product/service and predictive analytics, so that the organizations can focus on increasing their revenue.
          </p>
          
            <div className="why_zemantics-grid">
             <div className="why-card">
               <div className="why-icon">
                 <img src="/Assets/Icons/attitude-icon.svg" alt="Attitude" />
               </div>
               <div className="why-card-content">
                 <h3 className="why-card-title">Attitude</h3>
                 <p className="why-card-desc">That comes with a mindset that data based decision-making is the only way forward.</p>
               </div>
             </div>
             
             <div className="why-card">
               <div className="why-icon">
                 <img src="/Assets/Icons/mission-icon.svg" alt="Mission" />
               </div>
               <div className="why-card-content">
                 <h3 className="why-card-title">Mission</h3>
                 <p className="why-card-desc">Simplify data representation and provide actionable picture by applying industry leading AI, ML and Data Science techniques.</p>
               </div>
             </div>
             
             <div className="why-card">
               <div className="why-icon">
                 <img src="/Assets/Icons/experience-icon.svg" alt="Experience" />
               </div>
               <div className="why-card-content">
                 <h3 className="why-card-title">Experience</h3>
                 <p className="why-card-desc">Our team has executed some of the most mission critical, ground breaking projects for Financial, Telecom, healthcare, Pharma, Insurance & FMCG industries.</p>
               </div>
             </div>
             
             <div className="why-card">
               <div className="why-icon">
                 <img src="/Assets/Icons/innovation-icon.svg" alt="Innovation" />
               </div>
               <div className="why-card-content">
                 <h3 className="why-card-title">Innovation</h3>
                 <p className="why-card-desc">Our team at Zemantics is always striving for innovation and we have incubated an innovation lab where every idea, no matter what, is duly explored. This lab is the hub where</p>
                 <ul className="innovation-list">
                   <li>Our team is able to brainstorm models and new techniques</li>
                   <li>Incubate ideas using cutting edge tools</li>
                   <li>Build POCs that are essential to solving complex business problems</li>
                 </ul>
               </div>
             </div>
           </div>
        </div>
      </div>

             {/* Contact Us Section */}
       <div 
         id="contact-section"
         className="contact_us"
         style={{
           backgroundImage: `url('${process.env.PUBLIC_URL}/Assets/Home Page/reach-out-today-section-image.png')`
         }}
       >
        <div className="contact_us-content">
          {/* Left Side - Text Content */}
          <div className="contact_us-text">
            <h2 className="contact_us-title">Reach Out to Us Today!</h2>
            <p className="contact_us-desc">Our team is ready to help you move forward with the right solution. Let us know a bit about you, and we'll connect shortly!</p>
          </div>

                     {/* Right Side - Contact Form */}
           <div className="contact_us-form desktop-contact-form">
                           {submitStatus === 'success' && (
                <div className="homepage-success-message">
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="homepage-error-message">
                  {errorMessage}
                </div>
              )}

             <form className="contact-form" onSubmit={handleSubmit}>
               <div className="form-row">
                 <input 
                   type="text" 
                   name="name"
                   placeholder="Your Name" 
                   className="form-input"
                   value={formData.name}
                   onChange={handleInputChange}
                   required
                 />
                 <input 
                   type="email" 
                   name="email"
                   placeholder="Email" 
                   className="form-input"
                   value={formData.email}
                   onChange={handleInputChange}
                   required
                 />
               </div>
               <div className="form-row">
                 <input 
                   type="tel" 
                   name="phoneNumber"
                   placeholder="Phone" 
                   className="form-input"
                   value={formData.phoneNumber}
                   onChange={handleInputChange}
                   required
                 />
                 <select 
                   name="inquiry"
                   className="form-select"
                   value={formData.inquiry}
                   onChange={handleInputChange}
                   required
                 >
                  <option value="" disabled>Inquiry</option>
                  <option value="Artificial Intelligence And ML">Artificial Intelligence And ML</option>
                  <option value="Data Engineering">Data Engineering</option>
                  <option value="Generative AI">Generative AI</option>
                  <option value="Software Engineering For AI Products">Software Engineering For AI Products</option>
                  <option value="General Inquiry">General Inquiry</option>
                 </select>
               </div>
               <textarea 
                 name="message"
                 placeholder="Message" 
                 className="form-textarea"
                 rows={5}
                 value={formData.message}
                 onChange={handleInputChange}
                 required
               ></textarea>
               <button 
                 type="submit" 
                 className="form-submit"
                 disabled={isSubmitting}
               >
                 {isSubmitting ? 'Sending...' : 'Make a Request'}
               </button>
             </form>
           </div>

           {/* Mobile Contact Form */}
           <div className="contact_us-form mobile-contact-form">
             {submitStatus === 'success' && (
               <div className="homepage-success-message">
                 Thank you! Your message has been sent successfully. We'll get back to you soon.
               </div>
             )}

             {submitStatus === 'error' && (
               <div className="homepage-error-message">
                 {errorMessage}
               </div>
             )}

             <form className="contact-form homepage-mobile-contact-form-inner" onSubmit={handleSubmit}>
               <input 
                 type="text" 
                 name="name"
                 placeholder="Your Name" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.name}
                 onChange={handleInputChange}
                 required
               />
               <input 
                 type="email" 
                 name="email"
                 placeholder="Email" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.email}
                 onChange={handleInputChange}
                 required
               />
               <input 
                 type="tel" 
                 name="phoneNumber"
                 placeholder="Phone" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.phoneNumber}
                 onChange={handleInputChange}
                 required
               />
               <select 
                 name="inquiry"
                 className="form-select homepage-mobile-form-select"
                 value={formData.inquiry}
                 onChange={handleInputChange}
                 required
               >
                <option value="" disabled>Inquiry</option>
                <option value="Artificial Intelligence And ML">Artificial Intelligence And ML</option>
                <option value="Data Engineering">Data Engineering</option>
                <option value="Generative AI">Generative AI</option>
                <option value="Software Engineering For AI Products">Software Engineering For AI Products</option>
                <option value="General Inquiry">General Inquiry</option>
               </select>
               <textarea 
                 name="message"
                 placeholder="Message" 
                 className="form-textarea homepage-mobile-form-textarea"
                 rows={5}
                 value={formData.message}
                 onChange={handleInputChange}
                 required
               ></textarea>
               <button 
                 type="submit" 
                 className="form-submit homepage-mobile-form-submit"
                 disabled={isSubmitting}
               >
                 {isSubmitting ? 'Sending...' : 'Make a Request'}
               </button>
             </form>
           </div>
        </div>
      </div>

    </div>
  );
};

export default HomePage; 